/* eslint-disable max-lines */
import { DestroyRef, Signal, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { getState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { addEntity, setEntity, updateEntity, SelectEntityId } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Dayjs } from 'dayjs';
import { EMPTY, Observable, filter, forkJoin, map, of, pipe, switchMap, tap } from 'rxjs';
import { ApiErrorResponse, PullZone } from '@bn/api-interfaces';
import {
  BotDetectionExecutionMode,
  BotDetectionSensitivity,
  BotDetectionService,
  CreateShieldZoneRequest,
  CreateWafRateLimitRequest,
  CustomWafRule,
  DDoSService,
  GetRatelimitMetricsResponse,
  MetricsOverviewResolution,
  MetricsService,
  PaginationResponse,
  RateLimitRule,
  RateLimitingService,
  RatelimitMetrics,
  ReviewActionType,
  ShieldAccessListsApiStore,
  ShieldZoneRequest,
  ShieldZoneResponse,
  ShieldZoneService,
  TriggeredRuleItem,
  UpdateBotDetectionRequest,
  UpdateReviewTriggeredRuleRequest,
  UpdateShieldZoneRequest,
  WafMappedEnumList,
  WafRuleMainGroupModel,
  WAFService,
  UpdateCustomWAFRuleRequestParams,
  CreateCustomWafRuleRequest,
} from '@bn/app-shield';
import { PlanType } from '@bn/app-shield/enums';
import { StringNumberMap, delayOrNot } from '@bn/shared/entities/index';
import { StoreRequest, getStoreEvents, idKeyType, withDefaultRequestStatusState, withSingleEntity } from '@bn/shared/state-store';
import { DateTimeUtils } from '@bn/shared/utils';
import { BnToastrService } from '@bn/web-ui/toastr';
import { BotDetectionSensitivityLevelIndexes } from '../components/app-shield-sensitivity-picker/models';
import { WAFEngineConfig } from '../models';
import { PromoService } from '../openapi/api/promo.service';
import { GetShieldZoneBotDetectionMetricsResponse } from '../openapi/model/get-shield-zone-bot-detection-metrics-response';
import {
  MapShieldBotDetectionChartData,
  MapShieldOverviewChartsData,
  MapShieldPerRateLimitChartData,
  MapShieldRateLimitChartData,
  MapShieldWafTriggeredRuleChartData,
  MapShieldWafTriggeredRulesChartData,
  ShieldBotDetectionChartData,
  ShieldDdosChartData,
  ShieldOverviewChartData,
  ShieldPerRateLimitsChartData,
  ShieldRateLimitsChartData,
  ShieldWafTriggeredRuleChartData,
  ShieldWafTriggeredRulesChartData,
  enrichShieldEntity,
  mapShieldDdosChartData,
  prependCustomWafRulesId,
  stripCustomWafRulePrefix,
} from './helpers/shield-api-store.helper';

const events = getStoreEvents(
  'SHIELD_API',
  [
    'ADD_AI_RECOMMENDATION_TEXT',
    'GET_AI_RECOMMENDATION_TEXT',
    'BOT_DETECTION_METRICS',
    'GET_BOT_DETECTION_METRICS',
    'UPDATE_BOT_DETECTION_METRICS',
    'METRICS',
    'TRIGGERED_RULE_CHART_METRICS',
    'TRIGGERED_RULES_LIST',
    'TRIGGERED_RULES_SET_ITEM',
    'PULLZONE_ID',
    'SETUP',
    'RATE_LIMITS_LIST',
    'ADD',
    'UPDATE',
    'WAF_ENUMS',
    'WAF_ENGINE_CONFIG',
    'RATE_LIMITS',
    'WAF_CUSTOM_RULES',
    'WAF_CUSTOM_RULES_ITEM',
    'WAF_CUSTOM_RULES_ADD',
    'WAF_CUSTOM_RULES_UPDATE',
    'WAF_CUSTOM_RULES_DELETE',
    'RATE_LIMITS_ADD',
    'RATE_LIMITS_UPDATE',
    'RATE_LIMITS_DELETE',
    'WAF_RULES_LIST',
    'PROMO_STATE',
  ],
  ['SET', 'REQUIRED', 'ADD_CLEAR', 'UPDATE_CLEAR', 'SELECT', 'TOGGLE', 'DESELECT'],
);
const selectId: SelectEntityId<ShieldZoneResponse> = (shield) => shield.shieldZoneId ?? 0;

export type BotDetection = BotDetectionSensitivityLevelIndexes & {
  executionMode?: BotDetectionExecutionMode;
  fingerprintComplex?: boolean;
};

export type WafAvailableRuleGroups = {
  [key: number]: WafRuleMainGroupModel[];
};

export type ShieldPromoState = {
  currentPromos: string[];
  eligiblePromos: string[];
  enrolledPromos: string[];
  firstShieldZoneCreationDate: string | null;
};

export type ShieldStoreType = {
  pullZoneId: number;
  setupRequired: boolean | null;
  wafEnums: WafMappedEnumList[];
  ddosEnums: WafMappedEnumList[];
  engineConfig: WAFEngineConfig[];
  wafCustomRules: CustomWafRule[];
  botDetection: BotDetection | null;
  aiRecommendationText: string | null;
  rateLimits: RateLimitRule[];
  rateLimitsPagination: PaginationResponse | null;
  // wafAvailableRules: WafRuleMainGroupModel[];
  wafAvailableRuleGroups: WafAvailableRuleGroups;
  wafTriggeredRules: TriggeredRuleItem[] | null;
  totalCleanRequestsLimit: number | null;
  totalBillableRequests: number | null;
  promoState: ShieldPromoState | null;
  chartMetrics: ShieldChartMetricsType;
  detailedChartMetrics: {
    overviewChartData: ShieldOverviewChartData;
  };
  last24HoursChartMetrics: {
    wafMiniChartSeries: StringNumberMap;
    ddosMiniChartSeries: StringNumberMap;
    rateLimitingMiniChartSeries: StringNumberMap;
    botDetectionMiniChartSeries: StringNumberMap;
    accessListMiniChartSeries: StringNumberMap;
    uploadScanningMiniChartSeries: StringNumberMap;
  };
};

export type ShieldChartMetricsType = {
  ddosChartData: ShieldDdosChartData;
  rateLimitChartData: ShieldRateLimitsChartData;
  perRateLimitChartData: ShieldPerRateLimitsChartData;
  wafTriggeredRulesChartData: ShieldWafTriggeredRulesChartData;
  wafTriggeredRuleChartData?: ShieldWafTriggeredRuleChartData;
  botDetectionChartData?: ShieldBotDetectionChartData;
};

const initialState: ShieldStoreType = {
  pullZoneId: -1,
  setupRequired: null,
  wafEnums: [],
  ddosEnums: [],
  engineConfig: [],
  wafCustomRules: [],
  // wafAvailableRules: [],
  wafAvailableRuleGroups: {
    [PlanType.Basic]: [],
    [PlanType.Advanced]: [],
    [PlanType.Business]: [],
    [PlanType.Enterprise]: [],
  },
  rateLimits: [],
  rateLimitsPagination: null,
  aiRecommendationText: null,
  wafTriggeredRules: null,
  botDetection: null,
  totalCleanRequestsLimit: null,
  totalBillableRequests: null,
  promoState: null,
  chartMetrics: {
    ddosChartData: mapShieldDdosChartData(),
    botDetectionChartData: MapShieldBotDetectionChartData(),
    rateLimitChartData: MapShieldRateLimitChartData(),
    perRateLimitChartData: MapShieldPerRateLimitChartData(),
    wafTriggeredRulesChartData: MapShieldWafTriggeredRulesChartData(),
    wafTriggeredRuleChartData: MapShieldWafTriggeredRuleChartData(),
  },
  detailedChartMetrics: {
    overviewChartData: {
      chartSeries: MapShieldOverviewChartsData().chartSeries,
      breakdownData: MapShieldOverviewChartsData().breakdownData,
      total: MapShieldOverviewChartsData().total,
      hasData: MapShieldOverviewChartsData().hasData,
    },
  },
  last24HoursChartMetrics: {
    wafMiniChartSeries: MapShieldOverviewChartsData().wafMiniChartSeries,
    ddosMiniChartSeries: MapShieldOverviewChartsData().ddosMiniChartSeries,
    accessListMiniChartSeries: MapShieldOverviewChartsData().accessListMiniChartSeries,
    botDetectionMiniChartSeries: MapShieldOverviewChartsData().botDetectionMiniChartSeries,
    rateLimitingMiniChartSeries: MapShieldOverviewChartsData().rateLimitingMiniChartSeries,
    uploadScanningMiniChartSeries: MapShieldOverviewChartsData().uploadScanningMiniChartSeries,
  },
};

export const ShieldApiStore = signalStore(
  { providedIn: 'root' },
  withDevtools('Shield'),
  withState<ShieldStoreType>(initialState),
  // NOTE: Don't use withRouteParams, because it forces you to use store
  // as scoped service (provided in providers array).
  // Subscribe to route params change instead (edit-pull-zone.page in this case)
  // withRouteParams(({ pullZoneId }) => ({
  //   pullZoneId: Number(pullZoneId),
  // })),
  withState(initialState),
  withDefaultRequestStatusState(),
  withSingleEntity<ShieldZoneResponse>(),
  withMethods((store) => ({
    clearSubmitted: () => updateState(store, events.LIST.ADD_CLEAR, store.setDefault([StoreRequest.Add])),
    clearUpdated: () => updateState(store, events.LIST.UPDATE_CLEAR, store.setDefault([StoreRequest.Update])),
    selectEntity: (shieldId: idKeyType) => updateState(store, events.ITEM.SELECT, store.selectEntity(shieldId)),
    toggleEntity: (shieldId: idKeyType) => updateState(store, events.ITEM.TOGGLE, store.selectEntity(shieldId)),
    deselectEntity: (shieldId: idKeyType) => updateState(store, events.ITEM.DESELECT, store.selectEntity(shieldId)),
    getEngineConfig: (): Signal<WAFEngineConfig[]> => store.engineConfig,
    clearAiRecommendation: () => updateState(store, events.ADD_AI_RECOMMENDATION_TEXT.CLEAR, { aiRecommendationText: null }),
  })),

  /** Bot detection - chart methods */
  withMethods((store, metricsService = inject(MetricsService), destroyRef = inject(DestroyRef)) => ({
    getBotDetectionChartMetrics: rxMethod<{ shieldZoneId: number }>(
      pipe(
        switchMap(({ shieldZoneId }) => {
          updateState(store, events.BOT_DETECTION_METRICS.REQUEST, store.setLoading(StoreRequest.Item, ['botChartMetrics']));

          return metricsService.getShieldZoneBotDetectionMetrics({ shieldZoneId }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response: GetShieldZoneBotDetectionMetricsResponse) => {
                const { data, error } = response;

                if (error && !error?.success) {
                  updateState(
                    store,
                    events.BOT_DETECTION_METRICS.ERROR,
                    store.setErrorMessage(StoreRequest.Item, `Error while fetching Chart metrics`, ['botChartMetrics']),
                  );
                }

                updateState(
                  store,
                  events.BOT_DETECTION_METRICS.SUCCESS,
                  {
                    chartMetrics: {
                      ...getState(store).chartMetrics,
                      botDetectionChartData: MapShieldBotDetectionChartData(data),
                    },
                  },
                  store.setFinished(StoreRequest.Item, ['botChartMetrics']),
                );
              },
              error: ({ error }) =>
                updateState(
                  store,
                  events.BOT_DETECTION_METRICS.ERROR,
                  store.setErrorMessage(StoreRequest.Item, `Error while fetching Chart metrics ${error.message}`, ['botChartMetrics']),
                ),
            }),
          );
        }),
      ),
    ),
  })),

  /** Bot detection methods */
  withMethods((store, botDetectionService = inject(BotDetectionService), toastrService = inject(BnToastrService), destroyRef = inject(DestroyRef)) => ({
    getBotDetection: rxMethod<{ shieldZoneId: number; cleanRequest?: boolean }>(
      pipe(
        switchMap(({ shieldZoneId, cleanRequest }) => {
          if (store.selectedEntityId()() === shieldZoneId && Object.keys(getState(store).botDetection ?? {}).length && !cleanRequest) {
            return of(null);
          }

          updateState(store, events.GET_BOT_DETECTION_METRICS.REQUEST, store.setLoading(StoreRequest.Item, ['BotDetection']));
          return botDetectionService.getCurrentBotDetection({ shieldZoneId }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response) => {
                if (response?.error && !response?.error?.success) {
                  updateState(
                    store,
                    events.GET_BOT_DETECTION_METRICS.ERROR,
                    store.setErrorMessage(StoreRequest.Item, response.error?.message ?? 'There was an error while trying to get Bot Detection data.', [
                      'BotDetection',
                    ]),
                  );
                }

                if (shieldZoneId && !isNaN(shieldZoneId)) store.getBotDetectionChartMetrics({ shieldZoneId });

                updateState(
                  store,
                  events.GET_BOT_DETECTION_METRICS.SUCCESS,
                  {
                    botDetection: {
                      executionMode: response.data?.executionMode,
                      requestIntegrityLevel: response.data?.requestIntegrity.sensitivity,
                      ipAddressLevel: response.data?.ipAddress.sensitivity,
                      fingerprintAggressionLevel: response.data?.browserFingerprint.aggression,
                      fingerprintSensitivityLevel: response.data?.browserFingerprint.sensitivity,
                      fingerprintComplex: response.data?.browserFingerprint.complexEnabled || false,
                    },
                  },
                  store.setFinished(StoreRequest.Item, ['BotDetection']),
                );
              },
              error: ({ error }) => updateState(store, events.GET_BOT_DETECTION_METRICS.ERROR, store.setError(StoreRequest.Item, error, ['BotDetection'])),
            }),
          );
        }),
      ),
    ),

    updateBotDetection: rxMethod<{ shieldZoneId: number; data: BotDetection; customFields?: string[] }>(
      pipe(
        switchMap(({ shieldZoneId, data, customFields }) => {
          updateState(
            store,
            events.UPDATE_BOT_DETECTION_METRICS.REQUEST,
            store.setLoading(StoreRequest.Update, customFields ? customFields : ['BotDetection']),
          );
          const current = getState(store)?.botDetection;
          const merged = { ...current, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined)) };
          const updateBotDetectionRequest: UpdateBotDetectionRequest = {
            shieldZoneId,
            executionMode: merged.executionMode,
            requestIntegrity: { sensitivity: merged.requestIntegrityLevel as BotDetectionSensitivity },
            ipAddress: { sensitivity: merged.ipAddressLevel as BotDetectionSensitivity },
            browserFingerprint: {
              sensitivity: merged.fingerprintSensitivityLevel as BotDetectionSensitivity,
              aggression: merged.fingerprintAggressionLevel,
              complexEnabled: merged.fingerprintComplex,
            },
          };

          return botDetectionService.updateBotDetection({ shieldZoneId, updateBotDetectionRequest }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response) => {
                if (response?.error && !response?.error?.success) {
                  updateState(
                    store,
                    events.UPDATE_BOT_DETECTION_METRICS.ERROR,
                    store.setErrorMessage(
                      StoreRequest.Update,
                      response.error?.message ?? 'There was an error while trying to update data for Bot Detection.',
                      customFields ? customFields : ['BotDetection'],
                    ),
                  );
                }
                updateState(
                  store,
                  events.UPDATE_BOT_DETECTION_METRICS.SUCCESS,
                  {
                    botDetection: {
                      executionMode: response.data?.executionMode,
                      requestIntegrityLevel: response.data?.requestIntegrity.sensitivity,
                      ipAddressLevel: response.data?.ipAddress.sensitivity,
                      fingerprintAggressionLevel: response.data?.browserFingerprint.aggression,
                      fingerprintSensitivityLevel: response.data?.browserFingerprint.sensitivity,
                      fingerprintComplex: response.data?.browserFingerprint.complexEnabled ?? false,
                    },
                  },
                  store.setFinished(StoreRequest.Update, customFields ? customFields : ['BotDetection']),
                );

                toastrService.success('Bot detection configuration was updated.');
              },
              error: ({ error }) => {
                updateState(
                  store,
                  events.UPDATE_BOT_DETECTION_METRICS.ERROR,
                  store.setError(StoreRequest.Update, error, customFields ? customFields : ['BotDetection']),
                );
              },
            }),
          );
        }),
      ),
    ),
  })),

  withMethods((store, metricsService = inject(MetricsService), wafApiService = inject(WAFService), destroyRef = inject(DestroyRef)) => ({
    getWafRules: rxMethod<{ shieldZoneId: number; planType: number; forceFetch?: boolean }>(
      pipe(
        switchMap(({ shieldZoneId, planType, forceFetch }) => {
          if (!shieldZoneId) {
            return EMPTY;
          }

          const existingRules = store.wafAvailableRuleGroups()?.[planType] ?? [];

          // TODO: Decide, when do we want to re-fetch new rules
          if (!existingRules.length || forceFetch) {
            updateState(store, events.WAF_RULES_LIST.REQUEST, store.setLoading(StoreRequest.List, ['rules']));

            return wafApiService.getWAFRules({ shieldZoneId: +shieldZoneId }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (data) => {
                  const wafAvailableRuleGroups = {
                    ...store.wafAvailableRuleGroups(),
                    [planType]: data,
                  };

                  updateState(store, events.WAF_RULES_LIST.SUCCESS, { wafAvailableRuleGroups }, store.setFinished(StoreRequest.List, ['rules']));
                },
                error: ({ error }) => {
                  updateState(store, events.WAF_RULES_LIST.ERROR, store.setError(StoreRequest.List, error, ['rules']));
                },
              }),
            );
          }

          // If rules already exist and no force fetch, just finish loading
          updateState(store, events.WAF_RULES_LIST.SUCCESS, store.setFinished(StoreRequest.List, ['rules']));
          return EMPTY;
        }),
      ),
    ),

    readChartMetrics: rxMethod(
      pipe(
        switchMap(() => {
          const id = store.idSelected() as number;

          updateState(store, events.METRICS.REQUEST, store.setLoading(StoreRequest.Item, ['chartMetrics']));

          return id
            ? forkJoin({
                metricsOverview: metricsService.getShieldZoneMetricsOverview({ shieldZoneId: id }),
                metricsRateLimits: metricsService.getShieldZoneRateLimitMetrics({ shieldZoneId: id }) as Observable<GetRatelimitMetricsResponse>, // TODO: remove cast when swagger gets fixed
              }).pipe(
                takeUntilDestroyed(destroyRef),
                tapResponse({
                  next: ({ metricsOverview: { data: overviewData }, metricsRateLimits: { data: perRateLimitData } }) => {
                    const currentShield = store.entityMap()[id] as ShieldStoreType;

                    if (!currentShield) {
                      updateState(store, events.METRICS.ERROR, store.setErrorMessage(StoreRequest.Item, 'Error fetching chart metrics', ['chartMetrics']));

                      return;
                    }
                    updateState(
                      store,
                      events.METRICS.SUCCESS,
                      {
                        totalCleanRequestsLimit: overviewData?.totalCleanRequestsLimit,
                        totalBillableRequests: overviewData?.totalBillableRequests,
                        chartMetrics: {
                          ddosChartData: mapShieldDdosChartData(overviewData),
                          rateLimitChartData: MapShieldRateLimitChartData(overviewData),
                          perRateLimitChartData: MapShieldPerRateLimitChartData(
                            (perRateLimitData ?? []) as RatelimitMetrics[], // TODO: remove cast when swagger gets fixed
                          ),
                          wafTriggeredRulesChartData: MapShieldWafTriggeredRulesChartData(overviewData),
                        },
                      },
                      store.setFinished(StoreRequest.Item, ['chartMetrics']),
                    );
                  },
                  error: ({ error }) =>
                    updateState(
                      store,
                      events.METRICS.ERROR,
                      store.setErrorMessage(StoreRequest.Item, `Error while fetching Chart metrics ${error.message}`, ['chartMetrics']),
                    ),
                }),
              )
            : of(null);
        }),
      ),
    ),

    readDetailedChartMetrics: rxMethod<{
      id: ShieldZoneResponse['shieldZoneId'];
      dateTimeFrom?: Dayjs;
      dateTimeTo?: Dayjs;
      resolution?: MetricsOverviewResolution;
    }>(
      pipe(
        switchMap(({ dateTimeFrom: dateTimeFrom, dateTimeTo: dateTimeTo, resolution: resolution }) => {
          const id = store.idSelected() as number;
          updateState(store, events.METRICS.REQUEST, store.setLoading(StoreRequest.Item, ['detailedChartMetrics']));

          const startDate = dateTimeFrom ? dateTimeFrom.startOf('day').toISOString() : undefined;
          const endDate = dateTimeTo ? dateTimeTo.endOf('day').toISOString() : undefined;

          return id
            ? forkJoin({
                metricsOverview: metricsService.getShieldZoneDetailedMetricsOverview({
                  shieldZoneId: id,
                  startDate,
                  endDate,
                  resolution: resolution,
                }),
              }).pipe(
                takeUntilDestroyed(destroyRef),
                tapResponse({
                  next: ({ metricsOverview: { data: overviewData } }) => {
                    const currentShield = store.entityMap()[id] as ShieldStoreType;

                    if (!currentShield) {
                      updateState(
                        store,
                        events.METRICS.ERROR,
                        store.setErrorMessage(StoreRequest.Item, 'Error fetching chart metrics', ['detailedChartMetrics']),
                      );

                      return;
                    }

                    const { chartSeries, breakdownData, total, hasData } = MapShieldOverviewChartsData(overviewData);

                    updateState(
                      store,
                      events.METRICS.SUCCESS,
                      {
                        detailedChartMetrics: {
                          overviewChartData: {
                            chartSeries,
                            breakdownData,
                            total,
                            hasData,
                          },
                        },
                      },
                      store.setFinished(StoreRequest.Item, ['detailedChartMetrics']),
                    );
                  },
                  error: ({ error }) =>
                    updateState(
                      store,
                      events.METRICS.ERROR,
                      store.setErrorMessage(StoreRequest.Item, `Error while fetching Chart metrics ${error.message}`, ['detailedChartMetrics']),
                    ),
                }),
              )
            : of(null);
        }),
      ),
    ),

    readLast24HoursMetrics: rxMethod<{ id: ShieldZoneResponse['shieldZoneId'] }>(
      pipe(
        switchMap(() => {
          const id = store.idSelected() as number;
          updateState(store, events.METRICS.REQUEST, store.setLoading(StoreRequest.Item, ['last24HoursChartMetrics']));

          const now = DateTimeUtils.dayjsNow();
          const startDate = now.startOf('day').toISOString();
          const endDate = now.endOf('day').toISOString();

          return id
            ? metricsService
                .getShieldZoneDetailedMetricsOverview({
                  shieldZoneId: id,
                  startDate,
                  endDate,
                  resolution: 4,
                })
                .pipe(
                  takeUntilDestroyed(destroyRef),
                  tapResponse({
                    next: ({ data: overviewData }) => {
                      const currentShield = store.entityMap()[id] as ShieldStoreType;

                      if (!currentShield) {
                        updateState(
                          store,
                          events.METRICS.ERROR,
                          store.setErrorMessage(StoreRequest.Item, 'Error fetching last 24h metrics', ['last24HoursChartMetrics']),
                        );
                        return;
                      }

                      const {
                        wafMiniChartSeries,
                        ddosMiniChartSeries,
                        rateLimitingMiniChartSeries,
                        botDetectionMiniChartSeries,
                        accessListMiniChartSeries,
                        uploadScanningMiniChartSeries,
                      } = MapShieldOverviewChartsData(overviewData);

                      updateState(
                        store,
                        events.METRICS.SUCCESS,
                        {
                          last24HoursChartMetrics: {
                            wafMiniChartSeries,
                            ddosMiniChartSeries,
                            rateLimitingMiniChartSeries,
                            botDetectionMiniChartSeries,
                            accessListMiniChartSeries,
                            uploadScanningMiniChartSeries,
                          },
                        },
                        store.setFinished(StoreRequest.Item, ['last24HoursChartMetrics']),
                      );
                    },
                    error: ({ error }) =>
                      updateState(
                        store,
                        events.METRICS.ERROR,
                        store.setErrorMessage(StoreRequest.Item, `Error while fetching Last 24h metrics ${error.message}`, ['last24HoursChartMetrics']),
                      ),
                  }),
                )
            : of(null);
        }),
      ),
    ),

    getTriggeredRuleChartMetrics: rxMethod<{
      id: ShieldZoneResponse['shieldZoneId'];
      ruleId: string;
      customFields?: string[];
    }>(
      pipe(
        tap(({ customFields }) =>
          updateState(
            store,
            events.TRIGGERED_RULE_CHART_METRICS.REQUEST,
            store.setLoading(StoreRequest.Item, customFields ? customFields : ['TriggeredRuleMetrics']),
          ),
        ),
        switchMap(({ id, ruleId, customFields }) => {
          if (!id || !ruleId) return of(undefined);

          return metricsService.getWAFRuleMetrics({ shieldZoneId: id, ruleId: ruleId }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response) => {
                if (!response.error) {
                  updateState(
                    store,
                    events.TRIGGERED_RULE_CHART_METRICS.SUCCESS,
                    {
                      chartMetrics: {
                        ...getState(store)?.chartMetrics,
                        wafTriggeredRuleChartData: MapShieldWafTriggeredRuleChartData(response.data),
                      },
                    },
                    store.setFinished(StoreRequest.Item, customFields ? customFields : ['TriggeredRuleMetrics']),
                  );
                } else {
                  updateState(
                    store,
                    events.TRIGGERED_RULE_CHART_METRICS.ERROR,
                    store.setErrorMessage(
                      StoreRequest.Item,
                      response.error?.message ?? 'Error while trying to get waf triggered rules.',
                      customFields ? customFields : ['TriggeredRuleMetrics'],
                    ),
                  );
                }
              },
              error: () =>
                updateState(
                  store,
                  events.TRIGGERED_RULE_CHART_METRICS.ERROR,
                  store.setErrorMessage(
                    StoreRequest.Item,
                    'Error while trying to get waf triggered rules.',
                    customFields ? customFields : ['TriggeredRuleMetrics'],
                  ),
                ),
            }),
          );
        }),
      ),
    ),

    getTriggeredRules: rxMethod<{
      id: ShieldZoneResponse['shieldZoneId'];
      customFields?: string[];
    }>(
      pipe(
        tap(({ customFields }) =>
          updateState(store, events.TRIGGERED_RULES_LIST.REQUEST, store.setLoading(StoreRequest.List, customFields ? customFields : ['TriggeredRules'])),
        ),
        switchMap(({ id, customFields }) => {
          if (!id) return of(undefined);

          return wafApiService.getTriggeredWAFRules({ shieldZoneId: id }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response) => {
                updateState(
                  store,
                  events.TRIGGERED_RULES_LIST.SUCCESS,
                  {
                    wafTriggeredRules: response.triggeredRules?.sort((a, b) => Number(a.ruleId ?? 0) - Number(b.ruleId ?? 0)),
                  },
                  store.setFinished(StoreRequest.List, customFields ? customFields : ['TriggeredRules']),
                );
              },

              error: () =>
                updateState(
                  store,
                  events.TRIGGERED_RULES_LIST.ERROR,
                  store.setErrorMessage(StoreRequest.List, 'Error while trying to get waf triggered rules.', customFields ? customFields : ['TriggeredRules']),
                ),
            }),
          );
        }),
      ),
    ),
  })),
  withMethods(
    (
      store,
      shieldService = inject(ShieldZoneService),
      shieldAccessListStore = inject(ShieldAccessListsApiStore),
      rateLimitsService = inject(RateLimitingService),
      promoService = inject(PromoService),
      destroyRef = inject(DestroyRef),
    ) => ({
      getShield: rxMethod<{
        pullZoneId: PullZone['Id'];
        selectEntity?: boolean;
        cleanRequest?: boolean;
        cleanState?: boolean;
        includeChartMetrics?: boolean;
        includeTriggerdRules?: boolean;
      }>(
        pipe(
          filter(({ pullZoneId }) => pullZoneId !== -1),
          switchMap(({ selectEntity, pullZoneId, cleanRequest, cleanState, includeChartMetrics, includeTriggerdRules }) => {
            updateState(store, events.PULLZONE_ID.SET, { pullZoneId });

            const existingItem = Object.values(store.entityMap())
              .filter((entity) => entity.pullZoneId === pullZoneId)
              .at(0);

            if (existingItem && !store.isLoading(StoreRequest.Item)() && !cleanRequest) {
              if (selectEntity) store.selectEntity(existingItem.shieldZoneId);

              updateState(store, events.SETUP.REQUIRED, {
                setupRequired: false,
              });

              if (!getState(store).promoState || cleanRequest) {
                store.promoState();
              }

              return of(null);
            }

            if (cleanState) {
              updateState(store, events.SETUP.REQUIRED, { setupRequired: null });
              updateState(
                store,
                events.ITEM.CLEAR,
                {
                  aiRecommendationText: initialState.aiRecommendationText,
                  wafCustomRules: initialState.wafCustomRules,
                  rateLimits: initialState.rateLimits,
                  wafTriggeredRules: initialState.wafTriggeredRules,
                  promoState: initialState.promoState,
                },
                store.setDefault([StoreRequest.Item]),
              );
            }

            updateState(store, events.ITEM.REQUEST, store.setLoading(StoreRequest.Item));

            return forkJoin({
              data: shieldService.getShieldZoneConfigurationByPullZone({ pullZoneId }),
              includeChartMetrics: of(includeChartMetrics),
              includeTriggerdRules: of(includeTriggerdRules),
            }).pipe(
              takeUntilDestroyed(destroyRef),
              map(({ data, includeChartMetrics, includeTriggerdRules }) => ({
                entity: <ShieldZoneResponse>data.data ?? undefined,
                includeChartMetrics,
                includeTriggerdRules,
              })),
              tapResponse({
                next: ({ entity, includeChartMetrics, includeTriggerdRules }) => {
                  if (entity) {
                    if (entity.wafDisabledRules) entity.wafDisabledRules = stripCustomWafRulePrefix(entity.wafDisabledRules);
                    if (entity.wafLogOnlyRules) entity.wafLogOnlyRules = stripCustomWafRulePrefix(entity.wafLogOnlyRules);
                    if (entity.planType !== undefined) store.getWafRules({ shieldZoneId: Number(entity.shieldZoneId), planType: entity.planType });

                    if (selectEntity) store.selectEntity(entity.shieldZoneId);

                    const enrichedEntity = enrichShieldEntity(entity, store.getEngineConfig()());

                    updateState(store, events.SETUP.REQUIRED, {
                      setupRequired: false,
                    });
                    updateState(store, events.PULLZONE_ID.SET, { pullZoneId });
                    updateState(store, events.ITEM.SUCCESS, setEntity(enrichedEntity, { selectId }), store.setFinished(StoreRequest.Item));
                    if (cleanRequest || !getState(store).promoState) {
                      store.promoState();
                    }
                    if (includeChartMetrics) {
                      store.readChartMetrics({ id: entity.shieldZoneId });
                      store.readDetailedChartMetrics({ id: entity.shieldZoneId });
                    }
                    if (includeTriggerdRules) store.getTriggeredRules({ id: entity.shieldZoneId });

                    // Note: we have to request access list to get limits
                    if (entity.shieldZoneId) shieldAccessListStore.getAccessLists({ shieldZoneId: entity.shieldZoneId, cleanRequest: true });
                  } else {
                    if (selectEntity) store.deselectEntity();

                    updateState(store, events.SETUP.REQUIRED, { setupRequired: true });
                    updateState(store, events.ITEM.SUCCESS, store.setFinished(StoreRequest.Item));
                  }
                },
                error: ({ error }) => updateState(store, events.ITEM.ERROR, store.setErrorMessage(StoreRequest.Item, error)),
              }),
            );
          }),
        ),
      ),
      getShieldPromoState: rxMethod<void>(
        pipe(
          switchMap(() => {
            updateState(store, events.PROMO_STATE.REQUEST, store.setLoading(StoreRequest.Item, ['promoState']));

            return promoService.shieldPromoStateGet().pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (promoState: ShieldPromoState) => {
                  if (!promoState) {
                    updateState(store, events.PROMO_STATE.ERROR, store.setErrorMessage(StoreRequest.Item, 'Invalid promo response', ['promoState']));
                    return;
                  }

                  updateState(store, events.PROMO_STATE.SUCCESS, { promoState }, store.setFinished(StoreRequest.Item, ['promoState']));
                },
                error: ({ error, message }) => {
                  const errorMessage =
                    (typeof error?.message === 'string' && error.message) ||
                    (typeof message === 'string' && message) ||
                    'Error while fetching Shield promo data.';

                  updateState(store, events.PROMO_STATE.ERROR, store.setErrorMessage(StoreRequest.Item, errorMessage, ['promoState']));
                },
              }),
            );
          }),
        ),
      ),
      getRateLimits: rxMethod<{
        page: number;
        includeChartMetrics?: boolean;
      }>(
        pipe(
          switchMap(({ page, includeChartMetrics }) => {
            updateState(store, events.RATE_LIMITS_LIST.REQUEST, store.setLoading(StoreRequest.List, ['rateLimits']));

            const shieldId = store.selectedEntityId()();

            if (!shieldId) return EMPTY;

            return rateLimitsService.getRateLimits({ shieldZoneId: +shieldId, page: page || 1 }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (response) => {
                  const rateLimits = (response.data || []) as RateLimitRule[];
                  const rateLimitsPagination = (response.page || {}) as PaginationResponse;

                  updateState(
                    store,
                    events.RATE_LIMITS_LIST.SUCCESS,
                    { rateLimits, rateLimitsPagination },
                    store.setFinished(StoreRequest.List, ['rateLimits']),
                  );

                  if (includeChartMetrics) {
                    store.readChartMetrics({ id: Number(shieldId) });
                    store.readDetailedChartMetrics({ id: Number(shieldId) });
                  }
                },
                error: ({ error }) =>
                  updateState(
                    store,
                    events.RATE_LIMITS_LIST.ERROR,
                    store.setErrorMessage(StoreRequest.List, `Error while getting Shield WAF Rate limit enums ${error.message}`, ['rateLimits']),
                  ),
              }),
            );
          }),
        ),
      ),
    }),
  ),
  withMethods(
    (
      store,
      shieldService = inject(ShieldZoneService),
      shieldAccessListStore = inject(ShieldAccessListsApiStore),
      destroyRef = inject(DestroyRef),
      toastrService = inject(BnToastrService),
    ) => ({
      addShield: rxMethod<Partial<CreateShieldZoneRequest>>(
        pipe(
          switchMap((data) => {
            updateState(store, events.ADD.REQUEST, store.setLoading(StoreRequest.Add));
            const pullZoneId = getState(store).pullZoneId;
            if (data.shieldZone?.wafDisabledRules)
              data.shieldZone.wafDisabledRules = prependCustomWafRulesId(data.shieldZone.wafDisabledRules || [], store.wafCustomRules());

            if (data.shieldZone?.wafLogOnlyRules)
              data.shieldZone.wafLogOnlyRules = prependCustomWafRulesId(data.shieldZone.wafLogOnlyRules || [], store.wafCustomRules());

            return pullZoneId
              ? shieldService
                  .createShieldZone({
                    createShieldZoneRequest: {
                      pullZoneId,
                      ...data,
                    },
                  })
                  .pipe(
                    takeUntilDestroyed(destroyRef),
                    filter((response) => !!response.data?.shieldZone),
                    tapResponse({
                      next: (response) => {
                        const shield = response.data?.shieldZone;
                        const pullZoneId = response.data?.pullZoneId;

                        if (!shield) return;

                        if (pullZoneId === null || pullZoneId === undefined) return;

                        updateState(store, events.ADD.SUCCESS, addEntity(shield as ShieldZoneResponse, { selectId }), store.setFinished(StoreRequest.Add));

                        store.getShield({
                          pullZoneId: pullZoneId,
                          selectEntity: true,
                          includeChartMetrics: true,
                          includeTriggerdRules: true,
                        }); // HOTFIX: just re-get the shield to properly set all store data
                        // Note: we suspect everytime shield is added user is on current shield zone
                        // store.selectEntity(shield.shieldZoneId);  // HOTFIX
                      },
                      error: ({ error }) =>
                        updateState(store, events.ADD.ERROR, store.setErrorMessage(StoreRequest.Add, `Error while adding shield ${error.message}`)),
                    }),
                  )
              : of(null);
          }),
        ),
      ),
      updateShield: rxMethod<{
        id: UpdateShieldZoneRequest['shieldZoneId'];
        data: Partial<ShieldZoneRequest>;
        toastr?: { title?: string; message?: string };
        customFields?: string[];
        skipCleanRequest?: boolean;
      }>(
        pipe(
          tap(({ data, customFields }) => {
            updateState(store, events.UPDATE.REQUEST, store.setLoading(StoreRequest.Update, customFields ? customFields : Object.keys(data)));
          }),
          switchMap(({ id, data, toastr, customFields, skipCleanRequest }) => {
            data = { ...data, shieldZoneId: id };

            if (data.wafDisabledRules?.length) data.wafDisabledRules = prependCustomWafRulesId(data.wafDisabledRules || [], store.wafCustomRules());
            if (data.wafLogOnlyRules?.length) data.wafLogOnlyRules = prependCustomWafRulesId(data.wafLogOnlyRules || [], store.wafCustomRules());

            return store.isLoading(StoreRequest.Update)()
              ? shieldService
                  .updateShieldZone({
                    updateShieldZoneRequest: {
                      shieldZoneId: id,
                      shieldZone: data,
                    },
                  })
                  .pipe(
                    delayOrNot(),
                    switchMap((data) => of(<ShieldZoneResponse>data.data?.shieldZone ?? {})),
                    takeUntilDestroyed(destroyRef),
                    tapResponse({
                      next: (shield) => {
                        if (!toastr) {
                          toastrService.success('Shield configuration was updated.');
                        } else if (toastr.message || toastr.title) {
                          toastrService.success(toastr?.title ?? 'Shield configuration was updated.', toastr?.message ?? '');
                        }

                        if (shield.wafDisabledRules) shield.wafDisabledRules = stripCustomWafRulePrefix(shield.wafDisabledRules);
                        if (shield.wafLogOnlyRules) shield.wafLogOnlyRules = stripCustomWafRulePrefix(shield.wafLogOnlyRules);

                        updateState(
                          store,
                          events.UPDATE.SUCCESS,
                          updateEntity({ id, changes: shield }, { selectId }),
                          store.setFinished(StoreRequest.Update, customFields ? customFields : Object.keys(data)),
                        );

                        if (!skipCleanRequest) {
                          store.getShield({
                            pullZoneId: store.pullZoneId(),
                            cleanRequest: true,
                            includeChartMetrics: true,
                          });
                        }

                        // Note: always request bot detection in case of plan change
                        if (Object.keys(data).includes('planType') && shield.shieldZoneId) {
                          store.getBotDetection({ shieldZoneId: shield.shieldZoneId, cleanRequest: true });
                          shieldAccessListStore.getAccessLists({ shieldZoneId: shield.shieldZoneId, cleanRequest: true });
                        }
                      },
                      error: ({ error }) =>
                        updateState(
                          store,
                          events.UPDATE.ERROR,
                          store.setErrorMessage(
                            StoreRequest.Update,
                            `Error while updating shield ${error.message}`,
                            customFields ? customFields : Object.keys(data),
                          ),
                        ),
                    }),
                  )
              : of(null);
          }),
        ),
      ),
    }),
  ),
  /** WAF Rules methods */
  withMethods(
    (
      store,
      wafApiService = inject(WAFService),
      ddosApiService = inject(DDoSService),
      rateLimitsService = inject(RateLimitingService),
      toastrService = inject(BnToastrService),
      destroyRef = inject(DestroyRef),
    ) => ({
      getWafEnums: rxMethod(
        pipe(
          tap(() => {
            updateState(store, events.WAF_ENUMS.REQUEST, store.setLoading(StoreRequest.List, ['enums']));
          }),
          switchMap(() => wafApiService.getWAFEnumMappings()),
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const wafEnums = response.data || [];
              updateState(store, events.WAF_ENUMS.SUCCESS, { wafEnums }, store.setFinished(StoreRequest.List, ['enums']));
            },
            error: ({ error }) =>
              updateState(
                store,
                events.WAF_ENUMS.ERROR,
                store.setErrorMessage(StoreRequest.List, `Error while getting Shield WAF enums ${error.message}`, ['enums']),
              ),
          }),
        ),
      ),
      getDDoSEnums: rxMethod(
        pipe(
          switchMap(() => {
            updateState(store, events.WAF_ENUMS.REQUEST, store.setLoading(StoreRequest.List, ['ddosEnums']));
            return ddosApiService.getDDoSEnums();
          }),
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const ddosEnums = response.data || [];
              updateState(store, events.WAF_ENUMS.SUCCESS, { ddosEnums }, store.setFinished(StoreRequest.List, ['ddosEnums']));
            },
            error: ({ error }) =>
              updateState(
                store,
                events.WAF_ENUMS.ERROR,
                store.setErrorMessage(StoreRequest.List, `Error while getting Shield WAF DDOS enums ${error.message}`, ['ddosEnums']),
              ),
          }),
        ),
      ),
      getWafEngineConfig: rxMethod(
        pipe(
          switchMap(() => {
            updateState(store, events.WAF_ENGINE_CONFIG.REQUEST, store.setLoading(StoreRequest.List, ['wafEngineConfig']));

            return wafApiService.getWAFEngineConfiguration().pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (response) => {
                  const engineConfig = (response.data || []) as WAFEngineConfig[];

                  updateState(store, events.WAF_ENGINE_CONFIG.SUCCESS, { engineConfig }, store.setFinished(StoreRequest.List, ['wafEngineConfig']));
                },
                error: ({ error }) =>
                  updateState(
                    store,
                    events.WAF_ENGINE_CONFIG.ERROR,
                    store.setErrorMessage(StoreRequest.List, `Error while getting Shield WAF Engine Config ${error.message}`, ['wafEngineConfig']),
                  ),
              }),
            );
          }),
        ),
      ),
      getCustomWafRules: rxMethod<{
        shieldZoneId?: number;
      }>(
        pipe(
          switchMap((params) => {
            const shieldZoneId = params?.shieldZoneId ?? (store.selectedEntityId()() as number);

            if (!shieldZoneId) return EMPTY;

            updateState(store, events.WAF_CUSTOM_RULES.REQUEST, store.setLoading(StoreRequest.List, ['wafCustomRules']));

            return wafApiService.getCustomWAFRules({ shieldZoneId, page: 1, perPage: 9999 }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (getCustomWafRulesResponse) => {
                  updateState(
                    store,
                    events.WAF_CUSTOM_RULES.SUCCESS,
                    { wafCustomRules: getCustomWafRulesResponse.data || [] },
                    store.setFinished(StoreRequest.List, ['wafCustomRules']),
                  );
                },
                error: ({ error }) => updateState(store, events.WAF_CUSTOM_RULES.ERROR, store.setError(StoreRequest.List, error, ['wafCustomRules'])),
              }),
            );
          }),
        ),
      ),
      getWafRule: rxMethod<{
        id: number;
      }>(
        pipe(
          switchMap(({ id }) => {
            const shieldId = store.selectedEntityId()();
            if (!id || !shieldId) return EMPTY;

            updateState(store, events.WAF_CUSTOM_RULES_ITEM.CLEAR, store.setDefault([StoreRequest.Item]));
            updateState(store, events.WAF_CUSTOM_RULES_ITEM.REQUEST, store.setLoading(StoreRequest.Item, ['wafCustomRules']));

            return wafApiService.getCustomWAFRule({ id }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (entity) => {
                  const wafCustomRules = store.wafCustomRules();
                  const hasIndex = wafCustomRules.findIndex(({ id }) => id === entity.id);
                  if (hasIndex !== -1) delete wafCustomRules[hasIndex];

                  updateState(
                    store,
                    events.WAF_CUSTOM_RULES_ITEM.SUCCESS,
                    { wafCustomRules: [...wafCustomRules, entity] },
                    store.setFinished(StoreRequest.Item, ['wafCustomRules']),
                  );
                },
                error: ({ error }) =>
                  updateState(store, events.WAF_CUSTOM_RULES_ITEM.ERROR, store.setErrorMessage(StoreRequest.Item, error, ['wafCustomRules'])),
              }),
            );
          }),
        ),
      ),
      createWafRule: rxMethod<{ data: CreateCustomWafRuleRequest }>(
        pipe(
          switchMap(({ data }) => {
            updateState(store, events.WAF_CUSTOM_RULES_ADD.REQUEST, store.setLoading(StoreRequest.Add, ['wafCustomRules']));

            const shieldZoneId = Number(getState(store).idSelected);

            if (!shieldZoneId) return of(null);

            return wafApiService
              .createCustomWAFRule({
                createCustomWafRuleRequest: {
                  shieldZoneId,
                  ...data,
                },
              })
              .pipe(
                takeUntilDestroyed(destroyRef),
                tapResponse({
                  next: (wafRule: ShieldZoneResponse) => {
                    if ('errorResponse' in wafRule) {
                      const errorResponse = wafRule.errorResponse as ApiErrorResponse;

                      if (errorResponse && errorResponse.message) {
                        updateState(store, events.WAF_CUSTOM_RULES_ADD.ERROR, store.setFinished(StoreRequest.Add, ['wafCustomRules']));

                        toastrService.danger(errorResponse.message);
                        return;
                      }
                    }

                    const wafCustomRules = store.wafCustomRules();

                    updateState(
                      store,
                      events.WAF_CUSTOM_RULES_ADD.SUCCESS,
                      { wafCustomRules: [...wafCustomRules, wafRule] },
                      store.setFinished(StoreRequest.Add, ['wafCustomRules']),
                    );

                    toastrService.success('WAF rule was added successfully.');
                  },
                  error: ({ error }) => {
                    updateState(store, events.WAF_CUSTOM_RULES_ADD.ERROR, store.setError(StoreRequest.Add, error, ['wafCustomRules']));
                    toastrService.danger('There was an error while adding WAF rule.');
                  },
                }),
              );
          }),
        ),
      ),
      updateWafRule: rxMethod<{
        id: number;
        data: UpdateCustomWAFRuleRequestParams['updateCustomWafRuleRequest'];
        toastrTitle?: string;
        customFields?: string[];
      }>(
        pipe(
          switchMap(({ id, data, customFields }) => {
            updateState(store, events.WAF_CUSTOM_RULES_UPDATE.REQUEST, store.setLoading(StoreRequest.Update, customFields ? customFields : ['wafCustomRules']));

            if (!store.isLoading(StoreRequest.Update)()) return of(null);

            return wafApiService.updateCustomWAFRule({ id, updateCustomWafRuleRequest: data }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (wafRule) => {
                  if ('errorResponse' in wafRule) {
                    const errorResponse = wafRule.errorResponse as ApiErrorResponse;
                    if (errorResponse && errorResponse.message) {
                      toastrService.danger(errorResponse.message);
                      return;
                    }
                  }

                  const wafCustomRules = store.wafCustomRules().map((item) => (item.id === id ? { ...item, ...data } : item));

                  updateState(
                    store,
                    events.WAF_CUSTOM_RULES_UPDATE.SUCCESS,
                    { wafCustomRules },
                    store.setFinished(StoreRequest.Update, customFields ? customFields : ['wafCustomRules']),
                  );
                  toastrService.success('WAF Rule updated successfully.');
                },
                error: ({ error }) =>
                  updateState(
                    store,
                    events.WAF_CUSTOM_RULES_UPDATE.ERROR,
                    store.setError(StoreRequest.Update, error, customFields ? customFields : ['wafCustomRules']),
                  ),
              }),
            );
          }),
        ),
      ),
      deleteCustomWafRule: rxMethod<{ ruleId: number }>(
        pipe(
          switchMap(({ ruleId }) => {
            updateState(store, events.WAF_CUSTOM_RULES_DELETE.REQUEST, store.setLoading(StoreRequest.Delete, ['customWafRule']));

            return wafApiService.deleteCustomWAFRule({ id: ruleId }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: () => {
                  const wafCustomRules = store.wafCustomRules().filter((rule) => rule.id !== ruleId);
                  updateState(store, events.WAF_CUSTOM_RULES_DELETE.SUCCESS, { wafCustomRules }, store.setFinished(StoreRequest.Delete, ['customWafRule']));
                  toastrService.success('Custom WAF rule deleted successfully.');
                },
                error: ({ error }) => {
                  updateState(store, events.WAF_CUSTOM_RULES_DELETE.ERROR, store.setError(StoreRequest.Delete, error, ['customWafRule']));
                },
              }),
            );
          }),
        ),
      ),
      createRateLimitRule: rxMethod<Partial<CreateWafRateLimitRequest>>(
        pipe(
          switchMap((data) => {
            updateState(store, events.RATE_LIMITS_ADD.REQUEST, store.setLoading(StoreRequest.Add, ['rateLimits']));

            const shieldZoneId = Number(getState(store).idSelected);

            if (!shieldZoneId) return of(null);

            return rateLimitsService
              .createRateLimit({
                createWafRateLimitRequest: {
                  shieldZoneId,
                  ...data,
                },
              })
              .pipe(
                takeUntilDestroyed(destroyRef),
                tapResponse({
                  next: (wafRule: ShieldZoneResponse) => {
                    if ('errorResponse' in wafRule) {
                      const errorResponse = wafRule.errorResponse as ApiErrorResponse;
                      if (errorResponse && errorResponse.message) {
                        toastrService.danger(errorResponse.message);
                        return;
                      }
                    }

                    // const rateLimits = store.rateLimits();

                    updateState(
                      store,
                      events.RATE_LIMITS_ADD.SUCCESS,
                      // Note: We commented this part because if we fill it manually we are without chart data on table row
                      // { rateLimits: [...rateLimits, wafRule] },
                      store.setFinished(StoreRequest.Add, ['rateLimits']),
                    );
                    toastrService.success('Rate Limit Rule added successfully.');

                    // Note: call list of rate limits again to get data for chart
                    store.getRateLimits({ includeChartMetrics: true, page: store.rateLimitsPagination()?.currentPage || 1 });
                  },
                  error: ({ error }) => {
                    updateState(store, events.RATE_LIMITS_ADD.ERROR, store.setError(StoreRequest.Add, error, ['rateLimits']));
                  },
                }),
              );
          }),
        ),
      ),
      updateRateLimitRule: rxMethod<{
        id: number;
        data: Partial<RateLimitRule>;
        customFields?: string[];
      }>(
        pipe(
          switchMap(({ id, data, customFields }) => {
            updateState(store, events.RATE_LIMITS_UPDATE.REQUEST, store.setLoading(StoreRequest.Update, customFields ? customFields : ['rateLimits']));

            if (!store.isLoading(StoreRequest.Update)()) return of(null);

            return rateLimitsService.updateRateLimit({ id, updateWafRateLimitRequest: data }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (wafRule) => {
                  if ('errorResponse' in wafRule) {
                    const errorResponse = wafRule.errorResponse as ApiErrorResponse;
                    if (errorResponse && errorResponse.message) {
                      toastrService.danger(errorResponse.message);
                      return;
                    }
                  }

                  const rateLimits = store.rateLimits().map((item) => (item.id === id ? { ...item, ...data } : item)) as RateLimitRule[];

                  updateState(
                    store,
                    events.RATE_LIMITS_UPDATE.SUCCESS,
                    { rateLimits },
                    store.setFinished(StoreRequest.Update, customFields ? customFields : ['rateLimits']),
                  );
                  toastrService.success('Rate Limit Rule updated successfully.');
                },
                error: ({ error }) =>
                  updateState(store, events.RATE_LIMITS_UPDATE.ERROR, store.setError(StoreRequest.Update, error, customFields ? customFields : ['rateLimits'])),
              }),
            );
          }),
        ),
      ),
      deleteRateLimitRule: rxMethod<{ ruleId: number }>(
        pipe(
          switchMap(({ ruleId }) => {
            updateState(store, events.RATE_LIMITS_DELETE.REQUEST, store.setLoading(StoreRequest.Delete, ['rateLimits']));

            return rateLimitsService.deleteRateLimit({ id: ruleId }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: () => {
                  const rateLimits = store.rateLimits().filter((rule) => rule.id !== ruleId);
                  updateState(store, events.RATE_LIMITS_DELETE.SUCCESS, { rateLimits }, store.setFinished(StoreRequest.Delete, ['rateLimits']));

                  // Note: we need to update pagination and rate limits, also shield probably?
                  store.getRateLimits({ page: store.rateLimitsPagination()?.currentPage || 1 });

                  toastrService.success('Rate Limit Rule deleted successfully.');
                },
                error: ({ error }) => {
                  updateState(store, events.RATE_LIMITS_DELETE.ERROR, store.setError(StoreRequest.Delete, error, ['rateLimits']));
                },
              }),
            );
          }),
        ),
      ),

      setTriggeredRule: rxMethod<{
        id: ShieldZoneResponse['shieldZoneId'];
        data: UpdateReviewTriggeredRuleRequest;
        customFields?: string[];
        toastr?: { title?: string; message?: string };
      }>(
        pipe(
          tap(({ customFields }) =>
            updateState(
              store,
              events.TRIGGERED_RULES_SET_ITEM.REQUEST,
              store.setLoading(StoreRequest.Update, customFields ? customFields : ['TriggeredRules']),
            ),
          ),
          switchMap(({ id, data, toastr, customFields }) => {
            if (!id) return of(undefined);

            return wafApiService.updateTriggeredWAFRule({ shieldZoneId: id, updateReviewTriggeredRuleRequest: data }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (response) => {
                  if (response.success) {
                    if (!toastr) {
                      toastrService.success('Shield configuration was updated.');
                    } else if (toastr.message || toastr.title) {
                      toastrService.success(toastr?.title ?? 'Shield configuration was updated.', toastr?.message ?? '');
                    }

                    let changes: Partial<ShieldZoneResponse> = {};

                    // Note: Ignore - does nothing only removes item from triggered rules list
                    // if (data.action === ReviewActionType.NUMBER_0) changes = {};

                    // Log
                    if (data.action === ReviewActionType.LogOnly && data.ruleId)
                      changes = { wafLogOnlyRules: [...(store.selectedEntity()()?.wafLogOnlyRules || []), data.ruleId] };
                    // Disable
                    if (data.action === ReviewActionType.DisableRule && data.ruleId)
                      changes = { wafDisabledRules: [...(store.selectedEntity()()?.wafDisabledRules || []), data.ruleId] };

                    // Note: After post on any of triggered rules we remove this log from logs
                    updateState(
                      store,
                      events.TRIGGERED_RULES_SET_ITEM.SUCCESS,
                      {
                        wafTriggeredRules: store.wafTriggeredRules
                          ? store.wafTriggeredRules()?.filter((rule: TriggeredRuleItem) => rule.ruleId !== data.ruleId)
                          : [],
                      },
                      updateEntity({ id, changes }, { selectId }),
                      store.setFinished(StoreRequest.Update, customFields ? customFields : ['TriggeredRules']),
                    );
                  } else {
                    updateState(
                      store,
                      events.TRIGGERED_RULES_SET_ITEM.ERROR,
                      store.setErrorMessage(
                        StoreRequest.Update,
                        response.errorResponse?.message ?? 'Error while trying to get waf triggered rules.',
                        customFields ? customFields : ['TriggeredRules'],
                      ),
                    );
                  }
                },
                error: () =>
                  updateState(
                    store,
                    events.TRIGGERED_RULES_SET_ITEM.ERROR,
                    store.setErrorMessage(
                      StoreRequest.Update,
                      'Error while trying to get waf triggered rules.',
                      customFields ? customFields : ['TriggeredRules'],
                    ),
                  ),
              }),
            );
          }),
        ),
      ),
      getAIRecommendations: rxMethod<{ shieldZoneId: number; ruleId: string }>(
        pipe(
          switchMap(({ shieldZoneId, ruleId }) => {
            updateState(store, events.GET_AI_RECOMMENDATION_TEXT.REQUEST, store.setLoading(StoreRequest.Item, ['AI']));
            return wafApiService.getTriggeredRuleAIRecommendation({ shieldZoneId, ruleId }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (response) => {
                  if (!response.success) {
                    updateState(
                      store,
                      events.GET_AI_RECOMMENDATION_TEXT.ERROR,
                      store.setErrorMessage(
                        StoreRequest.Item,
                        response.errorResponse?.message ?? 'There was an error while trying to get AI recommendations.',
                        ['AI'],
                      ),
                    );
                  }

                  updateState(
                    store,
                    events.GET_AI_RECOMMENDATION_TEXT.SUCCESS,
                    { aiRecommendationText: response.recommendation },
                    store.setFinished(StoreRequest.Item, ['AI']),
                  );
                },
                error: ({ error }) => {
                  updateState(store, events.GET_AI_RECOMMENDATION_TEXT.ERROR, store.setError(StoreRequest.Item, error, ['AI']));
                },
              }),
            );
          }),
        ),
      ),
    }),
  ),

  withMethods((store, destroyRef = inject(DestroyRef)) => ({
    getShieldDefaults: rxMethod<void>(
      pipe(
        takeUntilDestroyed(destroyRef),
        tap(() => {
          // NOTE: Not sure if this is appropriate location to check for ff. But since we're in a hurry ...

          // Note: this functions are abusing list status state, all hooked to the same
          // we need to think about better implementation because now statuses overlap and we lose track
          store.getWafEnums(null);
          store.getDDoSEnums(null);
          store.getWafEngineConfig(null);

          // Note: untracked keeps this effect creation legal (NG0602) even when
          // getShieldDefaults() is invoked from within a reactive context
          untracked(() =>
            effect(() => {
              const shieldId = store.selectedEntityId()();

              untracked(() => {
                if (shieldId !== undefined) {
                  store.getCustomWafRules({});
                  store.getRateLimits({ page: 1 });
                  store.readChartMetrics({ id: Number(shieldId) });
                  store.readDetailedChartMetrics({ id: Number(shieldId) });
                  store.getTriggeredRules({ id: Number(shieldId) });
                  store.getShieldPromoState();
                }
              });
            }),
          );
        }),
      ),
    ),
  })),

  withHooks({
    onInit({ getEngineConfig, pullZoneId, getShield }) {
      effect(() => {
        const id = pullZoneId();
        const engineConfig = getEngineConfig()();

        untracked(() => {
          if (id && engineConfig.length) {
            getShield({
              pullZoneId: id,
              selectEntity: true,
            });
          }
        });
      });
    },
  }),
);
